// @ts-check
/* ═══════════════════════ TIDES (N3) ═══════════════════════
   NOAA Tides & Currents — gratis, ingen nøkkel, CORS-vennlig.
   Henter høyvann/lavvann for cruisedager i Alaska.
   ════════════════════════════════════════════════════════════ */

import { esc, fetchWithTimeout } from './utils.js';

const TRIP_START = new Date('2026-07-17T00:00:00+02:00');
const CACHE_TTL_MS = 12 * 60 * 60 * 1000;

// tripDay → NOAA-stasjon (kun cruisedager med havn).
// Sea-days droppes — tidevann er ikke meningsfullt midt i fjorden.
const PORTS = {
  4: { id: '9452749', name: 'Hoonah (Icy Strait)' },
  5: { id: '9452400', name: 'Skagway' },
  6: { id: '9452210', name: 'Juneau' },
  7: { id: '9450460', name: 'Ketchikan' },
};

export function hasTideData(tripDay){
  return tripDay in PORTS;
}

function isoFor(tripDay){
  const d = new Date(TRIP_START);
  d.setDate(d.getDate() + tripDay);
  return d.toISOString().slice(0, 10).replace(/-/g, '');
}

function cacheKey(stationId, ymd){
  return `tide:${stationId}:${ymd}`;
}

function readCache(key){
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL_MS) return null;
    return data;
  } catch { return null; }
}

function writeCache(key, data){
  try { localStorage.setItem(key, JSON.stringify({ data, ts: Date.now() })); } catch {}
}

/**
 * Henter high/low-tider for én havnedag.
 * @returns {Promise<{port:string, events:Array<{type:'H'|'L', time:string, m:number}>} | null>}
 */
export async function getPortTides(tripDay){
  const port = PORTS[tripDay];
  if (!port) return null;
  const ymd = isoFor(tripDay);
  const ck = cacheKey(port.id, ymd);
  const cached = readCache(ck);
  if (cached) return cached;

  // NOAA: hi/lo-prediksjoner i meter, lokal tidssone (lst_ldt = local standard / daylight time)
  const url = `https://api.tidesandcurrents.noaa.gov/api/prod/datagetter`
    + `?product=predictions&interval=hilo&datum=MLLW&units=metric`
    + `&time_zone=lst_ldt&format=json`
    + `&station=${port.id}&begin_date=${ymd}&end_date=${ymd}`;

  try {
    const r = await fetchWithTimeout(url);
    if (!r.ok) return null;
    const json = await r.json();
    const preds = json?.predictions;
    if (!Array.isArray(preds) || !preds.length) return null;
    const events = preds.map(p => ({
      type: /** @type {'H'|'L'} */ (p.type),
      time: p.t.slice(11, 16),   // "YYYY-MM-DD HH:MM" → "HH:MM"
      m: Number(p.v),
    }));
    const data = { port: port.name, events };
    writeCache(ck, data);
    return data;
  } catch { return null; }
}

/** Kompakt linje: "↑ 04:42 (4.8m) · ↓ 11:08 (0.6m) · ↑ 17:21 (5.1m) · ↓ 23:48 (0.4m)" */
export function formatTideLine(tides){
  if (!tides?.events?.length) return 'utilgjengelig';
  const parts = tides.events.map(e => {
    const arrow = e.type === 'H' ? '↑' : '↓';
    return `${arrow} <strong>${esc(e.time)}</strong> <span class="fj-row-sub">(${e.m.toFixed(1)} m)</span>`;
  });
  return parts.join(' · ');
}
